import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Chip, CircularProgress, TextField, Typography, Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessibleIcon from "@mui/icons-material/Accessible";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";

const HIRE = "#be123c";
const HIRE_LIGHT = "#fff1f2";

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ label, value, tone }) {
  return (
    <Box sx={{
      flex: "1 1 170px", minWidth: 170, p: 2, borderRadius: 2,
      border: "1px solid #e2e8f0", background: tone || "#fff",
    }}>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

const HaloHireOverviewPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await phappsRequest(API_PATHS.HALOHIRE_ADMIN_OVERVIEW, "GET");
      setRows(res?.data?.rows ?? []);
      setTotals(res?.data?.totals ?? null);
    } catch (err) {
      setError(getAdminApiErrorMessage(err) || "Failed to load HaloHire overview.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => String(r.chemistName || "").toLowerCase().includes(q));
  }, [rows, search]);

  const columns = [
    {
      field: "chemistName", headerName: "Chemist", flex: 1.6, minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{params.value}</Typography>
          {params.row.status === "Trial" && (
            <Chip label="Trial" size="small"
              sx={{ background: "#fef9c3", color: "#854d0e", fontWeight: 700, height: 20 }} />
          )}
        </Box>
      ),
    },
    { field: "state", headerName: "State", width: 150 },
    {
      field: "totalHirings", headerName: "Total hirings", width: 130,
      align: "right", headerAlign: "right", type: "number",
    },
    {
      field: "totalRevenue", headerName: "Total revenue", width: 150,
      align: "right", headerAlign: "right", type: "number",
      valueFormatter: (v) => money(v),
    },
    {
      field: "itemsAdded", headerName: "Items added", width: 130,
      align: "right", headerAlign: "right", type: "number",
    },
    {
      field: "overdueItems", headerName: "Overdue items", width: 150,
      align: "right", headerAlign: "right", type: "number",
      renderCell: (params) => (
        <Typography variant="body2" sx={{
          fontWeight: params.value > 0 ? 800 : 500,
          color: params.value > 0 ? "#991b1b" : "#0f172a",
        }}>
          {params.value}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5, background: HIRE_LIGHT,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <AccessibleIcon sx={{ color: HIRE, fontSize: 20 }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: HIRE }}>
          HaloHire Admin Overview
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}
          sx={{ ml: "auto", textTransform: "none" }}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: HIRE }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <StatCard label="Pharmacies" value={totals?.chemists ?? 0} />
            <StatCard label="Total hirings" value={totals?.totalHirings ?? 0} />
            <StatCard label="Total revenue" value={money(totals?.totalRevenue)} />
            <StatCard label="Items added" value={totals?.itemsAdded ?? 0} />
            <StatCard label="Overdue items" value={totals?.overdueItems ?? 0}
              tone={totals?.overdueItems > 0 ? HIRE_LIGHT : undefined} />
          </Box>

          <TextField
            size="small" placeholder="Filter by pharmacy name…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 320 }}
          />

          <DataGrid
            rows={filtered}
            columns={columns}
            getRowId={(r) => r.ChemistID}
            density="compact"
            autoHeight
            disableRowSelectionOnClick
            onRowClick={(params) => {
              if (params.row.ChemistUUID) {
                navigate(`/admin-functions/chemist/${params.row.ChemistUUID}`);
              }
            }}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: "totalHirings", sort: "desc" }] },
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{ ...pharmacyHaloDataGridSx, "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />

          {filtered.length === 0 && (
            <Alert severity="info">
              No pharmacies with an active HaloHire trial or subscription.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default HaloHireOverviewPage;
